import { useState, createContext, useContext, useEffect } from 'react';

import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { serverApis } from '../apis/serverApis';
import useToast, { TOAST_TYPE } from '../hooks/useToast';

// Constants
const SERVER_CONTEXT_ERROR = 'ServerContext must be used within a GPS Provider';

const ServerContext = createContext();

const ServerProvider = ({ children }) => {
  const { projectId, gpsId } = useParams();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [serverData, setServerData] = useState(null);

  const getServer = async () => {
    try {
      const { data: server } = await serverApis.getServer(projectId, gpsId);
      setServerData(server);
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('serverError', { ns: 'errors' }));
    }
  };

  useEffect(() => {
    if (!gpsId) return;
    const initialInstanceFetch = async () => {
      setLoading(true);
      await getServer();
      setLoading(false);
    };

    initialInstanceFetch();

    // Interval to periodically fetch gps instance.
    const gpsInterval = setInterval(getServer, 30000);
    // cleanup when navigating away from a gps-scoped page.
    return () => clearInterval(gpsInterval);
  }, [gpsId]);

  return (
    <ServerContext.Provider
      value={{
        loading: loading,
        server: serverData,
        refreshServer: getServer,
      }}
    >
      {children}
    </ServerContext.Provider>
  );
};

export const useServer = () => {
  const context = useContext(ServerContext);
  if (context === undefined) {
    throw new Error(SERVER_CONTEXT_ERROR);
  }
  return context;
};

ServerProvider.propTypes = {
  children: PropTypes.node,
};

export default ServerProvider;
